"use client";

import { createContext, useContext, useEffect, useRef } from "react";
import { z } from "zod";
import { useLocalStorage } from "./use-local-storage";

export const themeNameSchema = z.enum(["light", "dark"]);
export const themePreferenceSchema = z
  .union([themeNameSchema, z.literal("system")])
  .catch("system");

export type ThemeName = z.infer<typeof themeNameSchema>;
export type ThemePreference = z.infer<typeof themePreferenceSchema>;

const defaultPreference: ThemePreference = "system";

const ThemeContext = createContext({
  themePreference: defaultPreference as ThemePreference,
  isDarkMode: false,
  setThemePreference: (_theme: ThemePreference) => {},
});

function getSystemTheme(): ThemeName {
  return typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(theme: ThemeName) {
  document.documentElement.classList.remove("light", "dark");
  document.documentElement.classList.add(theme);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreference] = useLocalStorage(
    "theme",
    themePreferenceSchema,
    defaultPreference,
  );
  const hasAppliedInitialTheme = useRef(false);

  const currentTheme =
    themePreference === "system" ? getSystemTheme() : themePreference;

  useEffect(() => {
    // Avoid using View Transitions during initial mount or while the document
    // is not in a stable visible state. Chrome can reject these transitions.
    if (!hasAppliedInitialTheme.current) {
      applyTheme(currentTheme);
      hasAppliedInitialTheme.current = true;
      return;
    }

    if (
      !document.startViewTransition ||
      document.visibilityState !== "visible"
    ) {
      applyTheme(currentTheme);
      return;
    }

    try {
      const transition = document.startViewTransition(() =>
        applyTheme(currentTheme),
      );

      // Swallow aborted transition rejections so they don't surface as
      // unhandled promise rejections in error monitoring.
      void transition.ready.catch(() => {});
      void transition.finished.catch(() => {});
      void transition.updateCallbackDone.catch(() => {});
    } catch {
      applyTheme(currentTheme);
    }
  }, [currentTheme]);

  // Watch for system theme changes
  useEffect(() => {
    if (themePreference !== "system") {
      return;
    }

    const mql = window.matchMedia("(prefers-color-scheme: dark)");

    function handleSystemThemeChange() {
      applyTheme(getSystemTheme());
    }

    mql.addEventListener("change", handleSystemThemeChange);

    return () => {
      mql.removeEventListener("change", handleSystemThemeChange);
    };
  }, [themePreference]);

  return (
    <ThemeContext.Provider
      value={{
        themePreference,
        isDarkMode: currentTheme === "dark",
        setThemePreference,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
